import { useState, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { X, Loader2, FolderPlus, Check } from 'lucide-react'
import {
  createTheme,
  addThemeItems,
  listThemes,
  type FeedbackTheme,
} from '../../lib/feedbackApi'

interface ThemeDialogProps {
  open: boolean
  feedbackIds: number[]
  onClose: () => void
  onDone: () => void
}

export function ThemeDialog({ open, feedbackIds, onClose, onDone }: ThemeDialogProps) {
  const [mode, setMode] = useState<'choose' | 'new'>('choose')
  const [themes, setThemes] = useState<FeedbackTheme[]>([])
  const [loading, setLoading] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)

  // New theme fields
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')

  // Existing theme selection
  const [selectedThemeId, setSelectedThemeId] = useState<number | null>(null)

  useEffect(() => {
    if (!open) {
      setMode('choose')
      setName('')
      setDescription('')
      setSelectedThemeId(null)
      setError(null)
      setSuccess(false)
      return
    }

    setLoading(true)
    listThemes()
      .then((t) => {
        setThemes(t)
        if (t.length === 0) setMode('new')
      })
      .catch(() => {
        // Table may not exist yet — default to "create new" mode
        setThemes([])
        setMode('new')
      })
      .finally(() => setLoading(false))
  }, [open])

  async function handleCreateNew() {
    if (!name.trim() || submitting) return
    setSubmitting(true)
    setError(null)
    try {
      await createTheme({
        name: name.trim(),
        description: description.trim() || null,
        feedback_ids: feedbackIds,
      })
      setSuccess(true)
      setTimeout(() => { onDone(); onClose() }, 800)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create theme')
    } finally {
      setSubmitting(false)
    }
  }

  async function handleAddToExisting() {
    if (!selectedThemeId || submitting) return
    setSubmitting(true)
    setError(null)
    try {
      await addThemeItems(selectedThemeId, feedbackIds)
      setSuccess(true)
      setTimeout(() => { onDone(); onClose() }, 800)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add to theme')
    } finally {
      setSubmitting(false)
    }
  }

  if (!open) return null

  return createPortal(
    <>
      <div className="fixed inset-0 z-50 bg-black/30" onClick={onClose} />
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div
          className="bg-card border border-border rounded-xl shadow-2xl w-full max-w-md flex flex-col"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-border">
            <div className="flex items-center gap-2">
              <FolderPlus className="w-5 h-5 text-primary" />
              <h2 className="text-lg font-semibold">Group into Theme</h2>
            </div>
            <button onClick={onClose} className="p-1 rounded-lg hover:bg-secondary transition-colors">
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Body */}
          <div className="p-4 space-y-4">
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
              </div>
            ) : success ? (
              <div className="flex flex-col items-center justify-center py-8 gap-2">
                <div className="w-10 h-10 bg-green-500/10 rounded-full flex items-center justify-center">
                  <Check className="w-5 h-5 text-green-600" />
                </div>
                <p className="text-sm font-medium text-green-600">
                  {feedbackIds.length} item{feedbackIds.length !== 1 ? 's' : ''} grouped
                </p>
              </div>
            ) : (
              <>
                <p className="text-sm text-muted-foreground">
                  {feedbackIds.length} item{feedbackIds.length !== 1 ? 's' : ''} selected
                </p>

                {/* Mode tabs (only if there are existing themes) */}
                {themes.length > 0 && (
                  <div className="flex border border-border rounded-lg overflow-hidden">
                    <button
                      onClick={() => setMode('choose')}
                      className={`flex-1 px-3 py-2 text-sm font-medium transition-colors ${
                        mode === 'choose'
                          ? 'bg-primary text-primary-foreground'
                          : 'hover:bg-secondary'
                      }`}
                    >
                      Add to existing
                    </button>
                    <button
                      onClick={() => setMode('new')}
                      className={`flex-1 px-3 py-2 text-sm font-medium transition-colors ${
                        mode === 'new'
                          ? 'bg-primary text-primary-foreground'
                          : 'hover:bg-secondary'
                      }`}
                    >
                      Create new
                    </button>
                  </div>
                )}

                {mode === 'choose' && themes.length > 0 ? (
                  <div className="border border-border rounded-lg max-h-48 overflow-y-auto divide-y divide-border">
                    {themes.map((theme) => (
                      <button
                        key={theme.id}
                        onClick={() => setSelectedThemeId(theme.id)}
                        className={`w-full flex items-center gap-3 p-3 text-left transition-colors ${
                          selectedThemeId === theme.id
                            ? 'bg-primary/5 border-l-2 border-l-primary'
                            : 'hover:bg-secondary/50'
                        }`}
                      >
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">{theme.name}</p>
                          <p className="text-xs text-muted-foreground">
                            {theme.item_count} item{theme.item_count !== 1 ? 's' : ''} · {theme.total_votes} votes
                          </p>
                        </div>
                        {selectedThemeId === theme.id && (
                          <Check className="w-4 h-4 text-primary shrink-0" />
                        )}
                      </button>
                    ))}
                  </div>
                ) : (
                  <div className="space-y-3">
                    <div>
                      <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide block mb-1.5">
                        Theme name
                      </label>
                      <input
                        type="text"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder="e.g. GitHub Integration Requests"
                        className="w-full px-3 py-2 border border-border rounded-lg text-sm bg-background focus:outline-none focus:ring-2 focus:ring-primary"
                        autoFocus
                      />
                    </div>
                    <div>
                      <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide block mb-1.5">
                        Description (optional)
                      </label>
                      <textarea
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        placeholder="What this theme covers..."
                        rows={2}
                        className="w-full px-3 py-2 border border-border rounded-lg text-sm bg-background focus:outline-none focus:ring-2 focus:ring-primary resize-none"
                      />
                    </div>
                  </div>
                )}

                {error && (
                  <div className="p-3 text-sm bg-red-500/10 text-red-600 dark:text-red-400 rounded-lg">
                    {error}
                  </div>
                )}
              </>
            )}
          </div>

          {/* Footer */}
          {!success && !loading && (
            <div className="flex items-center justify-end gap-2 p-4 border-t border-border">
              <button
                onClick={onClose}
                className="px-4 py-2 text-sm rounded-lg hover:bg-secondary transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={mode === 'new' ? handleCreateNew : handleAddToExisting}
                disabled={
                  submitting ||
                  (mode === 'new' && !name.trim()) ||
                  (mode === 'choose' && !selectedThemeId)
                }
                className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
                {mode === 'new' ? 'Create Theme' : 'Add to Theme'}
              </button>
            </div>
          )}
        </div>
      </div>
    </>,
    document.body
  )
}
